import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import config from '../../config/config';
import '../../styles/SelamatAdminPage.css';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Home, Copy, AlertCircle } from 'react-feather';

const SelamatAdminPage = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const noTiket = params.get('no_tiket');
  const mode = params.get('mode'); // Get mode from URL parameters

  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfUrl2, setPdfUrl2] = useState('');
  const [baStatus, setBaStatus] = useState({ loading: true, available: false });
  const [rekomStatus, setRekomStatus] = useState({ loading: true, available: false });
  const [copySuccess, setCopySuccess] = useState(false);
  const navigate = useNavigate();

  // Configure axios interceptor for 401 handling
  axios.interceptors.response.use(
    response => response,
    error => {
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        navigate('/', { state: { sessionExpired: true } });
      }
      return Promise.reject(error);
    }
  );

  // Function to copy ticket number to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(noTiket).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    });
  };

  // Determine which documents to fetch based on mode
  const shouldFetchBA = mode === 'a'; // mode 'a' = approved (OK), show BA
  const shouldFetchRekom = mode === 'c'; // mode 'c' = cancelled/rejected (Tidak), show Rekom

  // Fetch PDF files
  useEffect(() => {
    const fetchPdfFiles = async () => {
      try {
        const token = localStorage.getItem('token');
        // Verify token is valid
        if (!token) {
          navigate('/');
          return;
        }

        try {
          const decodedToken = jwtDecode(token);
          if (!decodedToken.twoFactorVerified) {
            navigate('/');
            return;
          }
        } catch (e) {
          console.error('Invalid token:', e);
          navigate('/');
          return;
        }

        // Fetch Berita Acara PDF only if mode is 'a' (approved)
        if (shouldFetchBA) {
          try {
            setBaStatus({ loading: true, available: false });
            const response1 = await axios.get(`${config.API_URL}/template/getba?no_tiket=${noTiket}`, {
              headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/pdf',
              },
              responseType: 'blob',
            });

            if (response1.status === 200) {
              const pdfBlob = new Blob([response1.data], { type: 'application/pdf' });
              const url1 = URL.createObjectURL(pdfBlob);
              setPdfUrl(url1);
              setBaStatus({ loading: false, available: true });
            }
          } catch (error) {
            console.error('Error fetching Berita Acara PDF:', error);
            setBaStatus({ loading: false, available: false });
          }
        } else {
          // If mode is not 'a', set BA as not loading and not available
          setBaStatus({ loading: false, available: false });
        }

        // Fetch Rekomendasi PDF only if mode is 'c' (rejected)
        if (shouldFetchRekom) {
          try {
            setRekomStatus({ loading: true, available: false });
            const response2 = await axios.get(`${config.API_URL}/template/getrekom?no_tiket=${noTiket}`, {
              headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/pdf',
              },
              responseType: 'blob',
            });

            if (response2.status === 200) {
              const pdfBlob2 = new Blob([response2.data], { type: 'application/pdf' });
              const url2 = URL.createObjectURL(pdfBlob2);
              setPdfUrl2(url2);
              setRekomStatus({ loading: false, available: true });
            }
          } catch (error) {
            console.error('Error fetching Rekomendasi PDF:', error);
            setRekomStatus({ loading: false, available: false });
          }
        } else {
          // If mode is not 'c', set Rekom as not loading and not available
          setRekomStatus({ loading: false, available: false });
        }

      } catch (error) {
        console.error('Error in fetchPdfFiles:', error);
        setBaStatus({ loading: false, available: false });
        setRekomStatus({ loading: false, available: false });
      }
    };

    if (noTiket) {
      fetchPdfFiles();
    }

    // Clean up object URLs
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      if (pdfUrl2) URL.revokeObjectURL(pdfUrl2);
    };
  }, [noTiket, navigate, shouldFetchBA, shouldFetchRekom]);

  // Determine the appropriate title and message based on mode
  const getContentByMode = () => {
    if (mode === 'a') {
      return {
        title: 'Tiket Disetujui - Rekomendasi Integrasi',
        message: 'Kompilasi Informasi Geospasial Tematik telah disetujui. Berikut adalah Rekomendasi Integrasi dan Berbagi Pakai.'
      };
    } else if (mode === 'c') {
      return {
        title: 'Tiket Ditolak - Rekomendasi Perbaikan',
        message: 'Kompilasi Informasi Geospasial Tematik memerlukan perbaikan. Berikut adalah Rekomendasi Hasil Verifikasi.'
      };
    } else {
      return {
        title: 'Kompilasi Informasi Geospasial Tematik',
        message: 'Bapak Ibu Kementerian dan Lembaga, Berikut Adalah Tanda Terima Verifikasi Kompilasi Informasi Geospasial Tematik'
      };
    }
  };

  const content = getContentByMode();

  return (
    <div className="selamat-container">
      <div className="selamat-message">
        <div className="text-center mb-4">
          <img src="/logo.png" alt="Logo" className="img-fluid" style={{ maxWidth: '100px' }} />
        </div>

        <h2 className="selamat-title">
          <CheckCircle size={40} color="#28a745" className="mr-2" />
          {content.title}
        </h2>

        <div className="selamat-text">
          <p>{content.message} Tiket No.</p>

          <div className="ticket-number-container">
            <strong>{noTiket}</strong>
            <button
              className="copy-btn"
              onClick={copyToClipboard}
              title="Salin Nomor Tiket"
            >
              <Copy size={16} />
              {copySuccess ? 'Tersalin!' : 'Salin'}
            </button>
          </div>
        </div>

        <Link to="/admin" className="selamat-btn">
          <Home size={18} className="mr-2" />
          Beranda
        </Link>

        {/* Show loading indicator if any relevant document is still loading */}
        {((shouldFetchBA && baStatus.loading) || (shouldFetchRekom && rekomStatus.loading)) && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Memuat dokumen...</p>
          </div>
        )}

        {/* Show documents based on mode and availability */}
        {!((shouldFetchBA && baStatus.loading) || (shouldFetchRekom && rekomStatus.loading)) && (
          <>
            {!((shouldFetchBA && baStatus.available) || (shouldFetchRekom && rekomStatus.available)) ? (
              <div className="no-documents">
                <AlertCircle size={32} color="#e67e22" />
                <p>Tidak ada dokumen yang tersedia.</p>
              </div>
            ) : (
              <div className="pdf-preview-container mt-4">
                {/* Berita Acara Document - only show if mode is 'a' and available */}
                {shouldFetchBA && baStatus.available && (
                  <div className="pdf-box">
                    <h3 className="pdf-title">Rekomendasi Integrasi dan Berbagi Pakai</h3>
                    <iframe src={pdfUrl} width="100%" height="500px" title="Dokumen Rekomendasi Integrasi"></iframe>
                  </div>
                )}

                {/* Rekomendasi Document - only show if mode is 'c' and available */}
                {shouldFetchRekom && rekomStatus.available && (
                  <div className="pdf-box">
                    <h3 className="pdf-title">Rekomendasi Hasil Verifikasi</h3>
                    <iframe src={pdfUrl2} width="100%" height="500px" title="Dokumen Rekomendasi Hasil Verifikasi"></iframe>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SelamatAdminPage;